import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Moon, Sun, ArrowUpRight } from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/experience", label: "Experience", testid: "nav-experience-link" },
  { to: "/corporate-athlete", label: "Corporate Athlete 2.0", testid: "nav-corporate-athlete-link" },
  { to: "/insights", label: "Insights", testid: "nav-insights-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 inset-x-0 z-50 glass"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
        {/* Brand */}
        <Link
          to="/"
          data-testid="nav-brand-link"
          className="group flex items-center gap-2"
        >
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-muted-foreground hidden sm:block">
            VP //
          </span>
          <span className="font-display font-black text-lg tracking-tight">
            Vincent Palomarez
          </span>
        </Link>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                cn(
                  "px-3 py-2 text-sm rounded-sm transition-colors relative",
                  isActive
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-foreground" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            data-testid="theme-toggle-button"
            aria-label="Toggle theme"
            className="h-9 w-9 grid place-items-center rounded-sm border border-border hover:bg-secondary transition-colors"
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>
          <Link to="/contact" className="hidden sm:block">
            <Button
              data-testid="nav-contact-cta-button"
              variant="default"
              size="sm"
              className="rounded-sm gap-1 group"
            >
              Get in touch
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Button>
          </Link>

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="mobile-menu-toggle"
                aria-label="Open menu"
                className="lg:hidden h-9 w-9 grid place-items-center rounded-sm border border-border"
              >
                <Menu className="h-4 w-4" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[86%] sm:w-[380px] bg-background border-l border-border"
            >
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between pb-6 border-b border-border">
                  <span className="font-display font-black text-lg">Menu</span>
                  <button
                    onClick={() => setOpen(false)}
                    className="h-9 w-9 grid place-items-center rounded-sm border border-border"
                    aria-label="Close menu"
                    data-testid="mobile-menu-close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <nav className="flex flex-col mt-6 gap-1">
                  {NAV_LINKS.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      end={l.to === "/"}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-${l.testid}`}
                      className={({ isActive }) =>
                        cn(
                          "px-3 py-3 text-base rounded-sm border border-transparent",
                          isActive
                            ? "bg-secondary text-foreground border-border"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                        )
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="mt-auto pt-6 border-t border-border">
                  <p className="text-xs font-mono-ui tracking-widest uppercase text-muted-foreground">
                    Enterprise Sales · AI-Enabled Growth
                  </p>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="hairline-ai opacity-50" />
    </header>
  );
}
